/**
 * Hunts-specific date/time module — only the curated timezone list
 * lives here now. The generic datetime helpers (zone-aware
 * datetime-local round-trip, 12-hour time parsing, timezone-label
 * formatting, browser-zone detection) all moved to
 * `@startupquickstart/frontend` so tours-client can use the same
 * primitives.
 *
 * The curated list itself is opinionated (US-centric), so it stays
 * in the consuming app rather than the shared package.
 */
import { formatTimezoneLabel } from '@startupquickstart/frontend';

export interface TimezoneOption {
  /** IANA zone — what we store in the DB. */
  value: string;
  /** Short label (e.g. "ET"). */
  short: string;
  /** Full label without offset. */
  label: string;
}

const CURATED_TIMEZONES: readonly TimezoneOption[] = [
  { value: 'America/New_York', short: 'ET', label: 'Eastern (ET)' },
  { value: 'America/Chicago', short: 'CT', label: 'Central (CT)' },
  { value: 'America/Denver', short: 'MT', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', short: 'MST', label: 'Arizona (MST, no DST)' },
  { value: 'America/Los_Angeles', short: 'PT', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', short: 'AKT', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', short: 'HST', label: 'Hawaii (HST)' },
  { value: 'UTC', short: 'UTC', label: 'UTC' },
  { value: 'Europe/London', short: 'GMT/BST', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', short: 'CET', label: 'Central Europe (CET/CEST)' },
  { value: 'Asia/Tokyo', short: 'JST', label: 'Japan (JST)' },
  { value: 'Australia/Sydney', short: 'AET', label: 'Sydney (AET)' },
];

/**
 * Curated timezone options, each annotated with its current UTC
 * offset for the menu (e.g. `"Eastern (ET) — UTC−04:00"`).
 */
export function getTimezoneOptions(): readonly (TimezoneOption & {
  /** Combined menu label including current offset. */
  menuLabel: string;
})[] {
  return CURATED_TIMEZONES.map((opt) => ({
    ...opt,
    menuLabel: appendOffset(opt.label, opt.value),
  }));
}

function appendOffset(label: string, timezone: string): string {
  const match = formatTimezoneLabel(timezone).match(/\(([^)]+)\)/);
  return match ? `${label} — ${match[1]}` : label;
}

/**
 * Formats an ISO timestamp as a compact relative time (e.g. "5m ago").
 * Shared by the activity feeds and the submissions review surfaces.
 *
 * @param isoDate - ISO-8601 timestamp string.
 * @returns A short human-readable relative-time label.
 */
export function formatRelativeTime(isoDate: string): string {
  const diffSecs = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (diffSecs < 60) return 'just now';
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(isoDate).toLocaleDateString();
}

/**
 * Formats an ISO timestamp for table cells: "Jun 10, 2026" — compact and
 * scannable, unlike the verbose `toLocaleString` default. Pass
 * `withTime: true` for surfaces where the clock matters ("Jun 10, 3:45 PM").
 *
 * @param isoDate - ISO-8601 timestamp string (or null/undefined).
 * @param options - Set `withTime` to include the time of day.
 * @returns A short formatted date, or an em dash when absent.
 */
export function formatShortDate(
  isoDate: string | null | undefined,
  options: { withTime?: boolean } = {},
): string {
  if (!isoDate) return '—';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '—';
  if (options.withTime) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Ultra-compact relative time for dense ops surfaces ("<1m", "2m", "1h",
 * "3d") — no " ago" suffix. Use {@link formatRelativeTime} where space
 * allows a full phrase.
 *
 * @param isoDate - ISO-8601 timestamp string.
 * @returns A compact relative-time label.
 */
export function formatCompactRelativeTime(isoDate: string): string {
  const diffMins = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
  if (diffMins < 1) return '<1m';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
}

/**
 * Chat-message timestamp: clock time for today ("3:45 PM"), short date
 * with time for older messages ("Jun 10, 3:45 PM").
 *
 * @param isoDate - ISO-8601 timestamp string.
 * @returns A message-appropriate time label.
 */
export function formatMessageTime(isoDate: string): string {
  const date = new Date(isoDate);
  const isToday = date.toDateString() === new Date().toDateString();
  if (isToday) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
